var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose } from 'class-transformer';
import { IsArray, IsEnum, IsOptional, IsString } from 'class-validator';
import { CatalogAnlage } from '../enums/catalog-anlage.enum.js';
/**
 * Which conditional gate on a SDHMA prescription-need could not be decided from
 * the context the query was given. Reported so the caller can abstain *visibly*
 * (fixed decision #7) rather than silently treating an unevaluable condition as
 * met or unmet.
 */
export var PrescriptionNeedConditionKind;
(function (PrescriptionNeedConditionKind) {
    /** A required Sekundärcode whose presence could not be confirmed. */
    PrescriptionNeedConditionKind["SEKUNDAERCODE"] = "sekundaercode";
    /** An age bound that could not be checked (patient age unknown). */
    PrescriptionNeedConditionKind["AGE"] = "age";
    /** An Akutereignis window that could not be checked (event date unknown). */
    PrescriptionNeedConditionKind["AKUTEREIGNIS"] = "akutereignis";
    /** A KV-region scope that could not be checked (patient KV region unknown). */
    PrescriptionNeedConditionKind["KV_REGION"] = "kv_region";
})(PrescriptionNeedConditionKind || (PrescriptionNeedConditionKind = {}));
/**
 * One BVB/LHM qualification for an ICD, as resolved from the SDHMA release valid
 * at a date. The conditional gates (Sekundärcode, age bounds, Akutereignis
 * window, KV-region scope) are represented verbatim; the query only returns a
 * need whose *evaluable* gates are all satisfied. Gates it could not evaluate are
 * listed in `unevaluableConditions`.
 */
export class PrescriptionNeedDto {
    anlage;
    /** icd_code@V — the primary diagnosis this need applies to; null if none. */
    icdCode;
    /** sekundaercode@V — a required secondary diagnosis, or null. */
    sekundaercode;
    /** geltungsbereich_kv@V — the KV region this entry is scoped to; null = federal. */
    geltungsbereichKv;
    /** untere_altersgrenze — minimum patient age, or null. */
    untereAltersgrenze;
    /** obere_altersgrenze — maximum patient age, or null. */
    obereAltersgrenze;
    /** zeitraum_akutereignis — window after the acute event, or null. */
    zeitraumAkutereignis;
    /** hinweistext@V — informational note. */
    hinweistext;
    /** The allowed Kapitel/Diagnosegruppe combinations for this need. */
    kapitelDiagnosegruppen;
    /** Gates present on this need that the query could not evaluate. */
    unevaluableConditions;
}
/**
 * The lean projection of a {@link PrescriptionNeedDto} the client surfaces as a
 * badge (RS-009). BVB/LHM is informational only — a badge, never a violation
 * (fixed decision #6) — so the client needs just the Anlage, the qualifying ICD,
 * the optional Hinweistext, and any gates the query could not decide (so the
 * badge can carry a visible caveat rather than over-claiming — fixed decision #7).
 * The full catalog payload (durations, Kapitel/Diagnosegruppen) is deliberately
 * omitted from client views.
 */
export class PrescriptionNeedBadgeDto {
    anlage;
    icdCode;
    hinweistext;
    unevaluableConditions;
}
__decorate([
    Expose(),
    IsEnum(CatalogAnlage),
    __metadata("design:type", String)
], PrescriptionNeedBadgeDto.prototype, "anlage", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionNeedBadgeDto.prototype, "icdCode", void 0);
__decorate([
    Expose(),
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], PrescriptionNeedBadgeDto.prototype, "hinweistext", void 0);
__decorate([
    Expose(),
    IsArray(),
    IsEnum(PrescriptionNeedConditionKind, { each: true }),
    __metadata("design:type", Array)
], PrescriptionNeedBadgeDto.prototype, "unevaluableConditions", void 0);
/** Project a resolved prescription-need onto its client-facing badge shape. */
export function toPrescriptionNeedBadge(need) {
    const badge = new PrescriptionNeedBadgeDto();
    badge.anlage = need.anlage;
    badge.icdCode = need.icdCode;
    badge.hinweistext = need.hinweistext;
    badge.unevaluableConditions = need.unevaluableConditions;
    return badge;
}
//# sourceMappingURL=prescription-need.dto.js.map